import { useRef } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { IconExternalLink } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { extensionDetailPath } from '../../../../routes';

type SourceColumnProps = {
  hookId: number;
  name: string | undefined;
  isLoading: boolean;
};

export const SourceColumn = ({
  hookId,
  name,
  isLoading,
}: SourceColumnProps) => {
  const intl = useIntl();
  const skeletonWidth = useRef(R.randomInteger(30, 100)).current;

  // hookId 0 is not a valid hook id - it marks runs of jobs triggered
  // externally without any associated extension.
  if (hookId === 0) {
    return (
      <Stack
        sx={{
          maxWidth: '100%',
          height: '100%',
          justifyContent: 'center',
          p: 1,
        }}
      >
        <Typography variant="body2">
          {intl.formatMessage({
            id: 'features.activities.columns.source.external',
          })}
        </Typography>
      </Stack>
    );
  }

  const resolvedName = isLoading ? (
    <Skeleton width={skeletonWidth} />
  ) : (
    name ||
    intl.formatMessage({ id: 'features.activities.columns.extension.label' })
  );

  return (
    <Stack
      sx={{
        maxWidth: '100%',
        height: '100%',
        justifyContent: 'center',
        p: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ maxWidth: '100%', minWidth: 0 }}
      >
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          <span>{`${hookId}:`}</span>{' '}
          <Tooltip title={name ?? ''}>
            <Box component="span">{resolvedName}</Box>
          </Tooltip>
        </Typography>
        <IconButton
          size="small"
          component={RouterLink}
          to={extensionDetailPath(hookId)}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <SvgIcon fontSize="small">
            <IconExternalLink />
          </SvgIcon>
        </IconButton>
      </Stack>
    </Stack>
  );
};
